import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, TrendingUp, TrendingDown, MinusCircle, Loader2 } from "lucide-react";
import { apiClient } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { toast } from "sonner";

const POLL_MS = 60 * 1000;

function directionMeta(direction, alert_type) {
  // Handle new ma_signal alert type
  if (alert_type === "ma_signal") {
    if (direction === "ma_buy") {
      return {
        Icon: TrendingUp,
        pill: "bg-buy-soft text-buy-strong border-buy",
        label: "Compra pelas médias",
      };
    }
    return {
      Icon: TrendingDown,
      pill: "bg-sell-soft text-sell-strong border-sell",
      label: "Venda pelas médias",
    };
  }
  // Legacy recommendation_flip alerts
  const [, , to] = (direction || "").split("_");
  if (to === "buy") {
    return {
      Icon: TrendingUp,
      pill: "bg-buy-soft text-buy-strong border-buy",
      label: "Virou compra",
    };
  }
  if (to === "sell") {
    return {
      Icon: TrendingDown,
      pill: "bg-sell-soft text-sell-strong border-sell",
      label: "Virou venda",
    };
  }
  return {
    Icon: MinusCircle,
    pill: "bg-muted text-muted-foreground border-border",
    label: "Virou neutro",
  };
}

export default function AlertsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchCount = useCallback(async () => {
    try {
      const r = await apiClient.get("/alerts/unread-count");
      setCount(r.data?.count || 0);
    } catch {
      /* silent */
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/alerts", { params: { limit: 30 } });
      setItems(r.data || []);
    } catch {
      toast.error("Não foi possível carregar os alertas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(t);
  }, [fetchCount]);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) fetchList();
  };

  const handleClickAlert = async (alert) => {
    setOpen(false);
    if (!alert.is_read) {
      try {
        await apiClient.post(`/alerts/${alert.id}/read`);
      } catch {
        /* ignore */
      }
      setCount((c) => Math.max(0, c - 1));
    }
    navigate(`/acao/${alert.stock_id}`);
  };

  const handleMarkAll = async () => {
    try {
      await apiClient.post("/alerts/mark-all-read");
      setCount(0);
      setItems((prev) => prev.map((it) => ({ ...it, is_read: true })));
      toast.success("Todos os alertas foram marcados como lidos.");
    } catch {
      toast.error("Falha ao marcar alertas como lidos.");
    }
  };

  const unreadItems = useMemo(() => items.filter((it) => !it.is_read), [items]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-sidebar-accent/60 transition text-sidebar-foreground/80 hover:text-sidebar-foreground"
          data-testid="alerts-bell-trigger"
          aria-label="Alertas de mudança"
        >
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-sell text-white text-[9px] font-bold flex items-center justify-center font-mono-tab"
              data-testid="alerts-bell-badge"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="right"
        sideOffset={12}
        className="w-[380px] p-0 max-h-[520px] overflow-hidden flex flex-col"
        data-testid="alerts-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Alertas de mudança
            </div>
            <div className="text-sm font-semibold">
              Favoritos que trocaram de lado
            </div>
          </div>
          {unreadItems.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAll}
              data-testid="alerts-mark-all-read"
            >
              <Check className="w-3.5 h-3.5 mr-1" /> Marcar todos
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground p-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center" data-testid="alerts-empty">
              <Bell className="w-6 h-6 mx-auto text-muted-foreground opacity-50" />
              <div className="text-sm text-muted-foreground mt-3 max-w-[260px] mx-auto">
                Nenhum alerta por aqui. Favorite ações para receber notificação
                quando a recomendação virar entre compra e venda.
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((a) => {
                const meta = directionMeta(a.direction || "", a.alert_type);
                const Icon = meta.Icon;
                const isMa = a.alert_type === "ma_signal";
                return (
                  <li
                    key={a.id}
                    className={`px-4 py-3 hover:bg-accent cursor-pointer transition ${!a.is_read ? "bg-amber-50/60" : ""}`}
                    onClick={() => handleClickAlert(a)}
                    data-testid={`alert-item-${a.ticker.toLowerCase()}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`shrink-0 w-9 h-9 rounded-md border flex items-center justify-center ${meta.pill}`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-display font-bold text-sm">{a.ticker}</span>
                          <span className="text-[10px] uppercase tracking-[0.15em] border border-border rounded px-1.5 py-0.5 text-muted-foreground">
                            {a.exchange}
                          </span>
                          {isMa && (
                            <span className="text-[9px] uppercase tracking-[0.15em] bg-muted text-muted-foreground border border-border rounded px-1.5 py-0.5">
                              Médias
                            </span>
                          )}
                          {!a.is_read && (
                            <span
                              className="ml-auto text-[9px] uppercase tracking-[0.18em] bg-sell text-white px-1.5 py-0.5 rounded"
                              data-testid={`alert-unread-${a.ticker.toLowerCase()}`}
                            >
                              Novo
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {a.company_name}
                        </div>
                        <div className="mt-1.5 text-xs">
                          {isMa ? (
                            <span className={`font-semibold ${meta.pill.split(" ").find((c) => c.startsWith("text-")) || ""}`}>
                              {a.new_label}
                              {a.m1_days && a.m2_days ? ` · M1=${a.m1_days}d × M2=${a.m2_days}d` : ""}
                            </span>
                          ) : (
                            <>
                              <span className="text-muted-foreground">{a.previous_label}</span>
                              <span className="mx-1.5 text-muted-foreground">→</span>
                              <span className={`font-semibold ${meta.pill.split(" ").find((c) => c.startsWith("text-")) || ""}`}>
                                {a.new_label}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-mono-tab">
                          {fmtDate(a.analysis_date)}
                          {!isMa && ` · score ${Number(a.previous_score).toFixed(1)} → ${Number(a.new_score).toFixed(1)}`}
                          {isMa && a.m1_value != null && a.m2_value != null && ` · M1 ${Number(a.m1_value).toFixed(2)} · M2 ${Number(a.m2_value).toFixed(2)}`}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
